/**
 * Transition Wave Component
 * Custom HTML element with smooth left-to-right animation
 */
class TransitionWave extends HTMLElement {
  constructor() {
    super();
    this.isAnimating = false;
    this.animationDuration = 1000; // 1.4 seconds to match CSS
    this.easing = 'cubic-bezier(0.4, 0, 0.2, 1)'; // Material Design easing
      this.initializeStoredColor();
  }

  connectedCallback() {
    this.setupEventListeners();
    this.initializeStoredColor();
  }

  /**
   * Initialize stored color from current page state
   */
  initializeStoredColor() {
    // Small delay to ensure page is fully loaded
    setTimeout(() => {
      const currentColor = this.getCurrentPageFlavorColor();
      if (currentColor && currentColor !== '#ffffff') {
        this.storeCurrentFlavorColor(currentColor);
      }
    }, 100);
  }


  setupEventListeners() {
    // Listen for custom events to trigger animation
    document.addEventListener('transition-wave-start', () => {
      this.animateIn();
    });

    // Listen for product info events to trigger color transitions
    document.addEventListener('product-info:start', (event) => {
      this.handleProductInfoStart(event);
    });
    document.addEventListener('product-info:done', (event) => {
      this.handleProductInfoDone(event);
    });

    // Listen for our own completion event to update page color
    this.addEventListener('transition-wave-complete', (event) => {
      this.handleTransitionComplete(event);
    });
  }

  /**
   * Animate the wave from left to right
   */
  animateIn() {
    if (this.isAnimating) {
      return;
    }

    this.isAnimating = true;
    this.style.pointerEvents = 'auto';

    // Start from left (off-screen) - set initial state
    this.classList.remove('animating-out', 'reset');
    this.classList.add('animating-in');

    // Trigger animation to sweep right after a brief delay to let CSS transition start
    setTimeout(() => {
      this.classList.remove('animating-in');
      this.classList.add('animating-out');
    }, 50);

    // Reset animation state after completion
    setTimeout(() => {
      this.isAnimating = false;

      // Reset to starting position for next animation
      this.classList.remove('animating-out');
      this.classList.add('reset');

      this.dispatchEvent(new CustomEvent('transition-wave-complete'));
    }, this.animationDuration);
  }

  /**
   * Animate the wave back to the left
   */
  animateOut() {
    if (this.isAnimating) return;

    this.isAnimating = true;

    // Start from right
    this.classList.remove('animating-in', 'reset');
    this.classList.add('animating-out');

    // Animate back to left (off-screen) after a brief delay
    setTimeout(() => {
      this.classList.remove('animating-out');
      this.classList.add('reset');
    }, 50);

    // Reset state after animation
    setTimeout(() => {
      this.style.pointerEvents = 'none';
      this.isAnimating = false;
      this.dispatchEvent(new CustomEvent('transition-wave-reset'));
    }, this.animationDuration);
  }
  

  /**
   * Handle product-info:start event to trigger color transition
   */
  handleProductInfoStart(event) {
    // Skip if already animating
    if (this.isAnimating) return;

    // Get the new color from the clicked variant immediately
    const targetElement = event.detail?.target;
    const newColor = this.getAccentColorFromTarget(targetElement);

    if (!newColor) {
      return;
    }

    // Get the current page color as the old color
    const oldColor = this.getCurrentPageFlavorColor();

    this.animateInWithColorChange(oldColor, newColor);
  }

  /**
   * Handle product-info:done event to trigger color transition after product loads
   */
  handleProductInfoDone(event) {
    // Skip if already animating
    if (this.isAnimating) return;

    // Get the target element from the done event
    const targetElement = event.detail?.target;

    if (!targetElement) {
      console.warn('No target element provided in product-info:done event');
      return;
    }

    // Get the new color from the target variant
    const newColor = this.getAccentColorFromTarget(targetElement);

    if (!newColor) {
      console.warn('No accent color found for transition wave', targetElement);
      return;
    }

    // Get the current page color as the old color
    const oldColor = this.getCurrentPageFlavorColor();

    this.animateInWithColorChange(oldColor, newColor);
  }

  /**
   * Extract accent color from the clicked variant slider item
   */
  getAccentColorFromTarget(targetElement) {
    if (!targetElement) {
      console.warn('No target element provided');
      return null;
    }

    // Look for the variant slider item that was clicked
    const variantItem = targetElement.closest('.variant-slider__item, [data-accent-color], .variant-item, .product-form__input');

    if (!variantItem) {
      console.warn('No variant item found', targetElement);
      return null;
    }

    // Get the flavor color from the CSS variable
    const flavorColor = this.getFlavorColorFromElement(variantItem);

    if (flavorColor) {
      return flavorColor;
    }

    // Fallback to data attributes
    const fallbackColor = variantItem.dataset.accentColor ||
                         variantItem.dataset.color ||
                         this.getAccentColorFromStyle(variantItem);

    return fallbackColor;
  }

  /**
   * Get flavor color from element's CSS custom property
   */
  getFlavorColorFromElement(element) {
    // First try to get it from the style attribute (inline styles)
    const styleAttribute = element.getAttribute('style');
    if (styleAttribute) {
      const flavorMatch = styleAttribute.match(/--flavor-color:\s*([^;]+)/);
      if (flavorMatch && flavorMatch[1]) {
        return flavorMatch[1].trim();
      }
    }

    // Fallback to computed style
    const computedStyle = getComputedStyle(element);
    const flavorColor = computedStyle.getPropertyValue('--flavor-color').trim();

    // Check if it's a valid color
    if (flavorColor && flavorColor !== '' && flavorColor !== 'initial' && flavorColor !== 'inherit') {
      return flavorColor;
    }

    return null;
  }

  /**
   * Get accent color from element's computed style
   */
  getAccentColorFromStyle(element) {
    const computedStyle = getComputedStyle(element);
    const backgroundColor = computedStyle.backgroundColor;

    // Check if it's a valid color (not transparent or default)
    if (backgroundColor && backgroundColor !== 'transparent' && backgroundColor !== 'rgba(0, 0, 0, 0)') {
      return backgroundColor;
    }

    return null;
  }

  /**
   * Get current page flavor color
   */
  getCurrentPageFlavorColor() {
    // First try to get it from our stored value
    const storedColor = this.dataset.currentFlavorColor;
    if (storedColor) {
      return storedColor;
    }

    const computedStyle = getComputedStyle(document.documentElement);
    const currentColor = computedStyle.getPropertyValue('--flavor-color').trim();

    // Return the current color or default to white
    const finalColor = currentColor || '#ffffff';
    return finalColor;
  }

  /**
   * Store the current flavor color for persistence
   */
  storeCurrentFlavorColor(color) {
    this.dataset.currentFlavorColor = color;
  }

  /**
   * Apply color to the transition wave
   */
  applyWaveColor(color) {
    this.style.setProperty('--wave-color', color);
  }


  /**
   * Animate wave with simple color change
   */
  animateInWithColorChange(oldColor, newColor) {
    if (this.isAnimating) return;

    this.isAnimating = true;
    this.style.pointerEvents = 'auto';

    // Set the OLD color for the sweep animation
    this.applyWaveColor(oldColor);

    // Trigger the sweep animation
    this.animateIn(() => {
      // After sweep completes, change to new color
      this.applyWaveColor(newColor);

      // Animation complete - set isAnimating to false first
      this.isAnimating = false;

      // Dispatch complete event (page color update handled by event listener)
      this.dispatchEvent(new CustomEvent('transition-wave-complete', {
        detail: { newColor: newColor, oldColor: oldColor }
      }));
    });
  }

  /**
   * Animate the wave from left to right with callback
   */
  animateIn(onComplete) {
    // Start from left (off-screen) - set initial state
    this.classList.remove('animating-out', 'reset');
    this.classList.add('animating-in');

    // Trigger animation to sweep to the right after a brief delay
    setTimeout(() => {
      this.classList.remove('animating-in');
      this.classList.add('animating-out');
    }, 50);

    // Call completion callback and reset position
    setTimeout(() => {
      if (onComplete) onComplete();

      // Reset to starting position for next animation
      this.classList.remove('animating-out');
      this.classList.add('reset');
    }, this.animationDuration);
  }

  /**
   * Handle transition complete event - updates page color after animation
   */
  handleTransitionComplete(event) {
    const { newColor, oldColor } = event.detail;

    // Update the page's flavor color after animation completes
    setTimeout(() => {
      this.updatePageFlavorColor(newColor);
    }, this.animationDuration);
  }

  /**
   * Update the page's flavor color inline style
   */
  updatePageFlavorColor(color) {
    // Also update the root variables to ensure consistency
    const root = document.documentElement;
    if (root) {
      root.style.setProperty('--flavor-color', color);
    }

    // Store the color for persistence across re-renders
    this.storeCurrentFlavorColor(color);
  }
}

// Register the custom element
try {
  customElements.define('transition-wave', TransitionWave);
} catch (error) {
  console.error('❌ TransitionWave: Failed to register custom element:', error);
}


// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = TransitionWave;
}
